import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { DatasetSummary } from '../../shared/types.ts';
import { fetchJson } from '../lib/api.ts';

interface AuthSession {
  authRequired: boolean;
  authenticated: boolean;
  username: string | null;
}

interface DatasetState {
  summary: DatasetSummary | null;
  loading: boolean;
  error: string | null;
  session: AuthSession;
  refreshing: boolean;
  refreshError: string | null;
  refreshNotice: string | null;
  reloadSummary: () => Promise<void>;
  refreshData: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const defaultSession: AuthSession = { authRequired: false, authenticated: true, username: null };

const DatasetContext = createContext<DatasetState>({
  summary: null,
  loading: true,
  error: null,
  session: defaultSession,
  refreshing: false,
  refreshError: null,
  refreshNotice: null,
  reloadSummary: async () => undefined,
  refreshData: async () => undefined,
  login: async () => undefined,
  logout: async () => undefined,
});

export function DatasetProvider(props: { children: ReactNode }) {
  const [summary, setSummary] = useState<DatasetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<AuthSession>(defaultSession);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);

  const loadSession = useCallback(async (): Promise<AuthSession> => {
    try {
      return await fetchJson<AuthSession>('/api/auth/session');
    } catch {
      return defaultSession;
    }
  }, []);

  const reloadSummary = useCallback(async () => {
    const nextSession = await loadSession();
    setSession(nextSession);
    if (nextSession.authRequired && !nextSession.authenticated) {
      setSummary(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const value = await fetchJson<DatasetSummary>('/api/dataset/summary');
      setSummary(value);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to load dataset summary');
    } finally {
      setLoading(false);
    }
  }, [loadSession]);

  useEffect(() => {
    void reloadSummary();
  }, [reloadSummary]);

  const refreshData = useCallback(async () => {
    if (refreshing) {
      return;
    }
    setRefreshing(true);
    setRefreshError(null);
    setRefreshNotice(null);
    try {
      const body = await fetchJson<{
        ok: boolean;
        snapshot: {
          sourceRoundCount: number;
          latestDetectedAtUtc: string | null;
          createdAtUtc: string;
        };
        summary: DatasetSummary;
      }>('/api/dataset/refresh', { method: 'POST' });
      setSummary(body.summary);
      setRefreshNotice(
        `Updated ${body.snapshot.sourceRoundCount.toLocaleString('en-US')} rounds. Latest round: ${body.snapshot.latestDetectedAtUtc ?? '—'}. Snapshot: ${body.snapshot.createdAtUtc}`,
      );
    } catch (cause) {
      setRefreshError(cause instanceof Error ? cause.message : 'Data refresh failed.');
    } finally {
      setRefreshing(false);
    }
  }, [refreshing]);

  const login = useCallback(
    async (username: string, password: string) => {
      await fetchJson('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      await reloadSummary();
    },
    [reloadSummary],
  );

  const logout = useCallback(async () => {
    await fetchJson('/api/auth/logout', { method: 'POST' });
    setSummary(null);
    await reloadSummary();
  }, [reloadSummary]);

  const value = useMemo(
    () => ({
      summary,
      loading,
      error,
      session,
      refreshing,
      refreshError,
      refreshNotice,
      reloadSummary,
      refreshData,
      login,
      logout,
    }),
    [
      summary,
      loading,
      error,
      session,
      refreshing,
      refreshError,
      refreshNotice,
      reloadSummary,
      refreshData,
      login,
      logout,
    ],
  );
  return <DatasetContext.Provider value={value}>{props.children}</DatasetContext.Provider>;
}

export function useDataset(): DatasetState {
  return useContext(DatasetContext);
}
